import { Close } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
  Typography
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";

import { useDistinctUsers } from "../../../../data/hooks/useDistinctUsers";
import { useLensProtocols } from "../../../../data/hooks/useLensProtocols";
import { generateSystemPrompt, PRODUCTION_MODEL } from "../../../../services/claude";
import {
  DOCUMENT_NAME_AGENT_SCORECARDS_SUMMARY,
  DOCUMENT_NAME_TEAM_BENCHMARKS,
  submitWeeklyCoachingReport,
  type WeeklyCoachingReportParameters
} from "../../../../services/backgroundJobs";
import { logError } from "../../../../utils/error";
import { CONVERSATION_LENS } from "../../../../utils/terminology";
import { getTimeRangeFilter } from "../../../../utils/timerange";
import { getUserTimezone } from "../../../../utils/timezone";
import { useAccount } from "../../../account/hooks/useAccount";
import {
  getSavedSystemPrompt,
  hasCustomSystemPrompt
} from "../../../account/utils/customSystemPromptStorage";
import { useAuth } from "../../../auth/AuthContext";
import { useSelectedGroups } from "../../../auth/hooks/useSelectedGroups";
import { DEFAULT_MAX_TOKENS, DEFAULT_TEMPERATURE } from "../../constants/modelDefaults";
import { useAttributeExamples } from "../../hooks/useAttributeExamples";
import { buildPerformanceAnalysisData } from "../../utils/buildPerformanceAnalysisData";
import { convertToAgentPerformanceAnalysis } from "../../utils/markdownConverter";
import { useCoachingHubPrompt } from "../../hooks/useCoachingHubPrompt";
import { useExistingCoachingArtifacts } from "../../hooks/useExistingCoachingArtifacts";
import { useCoachingContextStore } from "../../store/coachingContextStore";
import { useCoachingGeneratorStore } from "../../store/coachingGeneratorStore";
import {
  useWizardStore,
  WIZARD_STEP_GENERATE,
  WIZARD_STEP_USER,
  WIZARD_STEPS,
  WizardStep
} from "../../store/wizardStore";
import { AutoGenerateParams, ReportRequestedParams } from "../../types";
import GenerateReport from "./GenerateReport";
import SelectLens from "./SelectLens";
import SelectUser from "./SelectUser";
import SelectWeek from "./SelectWeek";

interface WeeklyCoachingWizardProps {
  open: boolean;
  onClose: () => void;
  onReportRequested?: (params: ReportRequestedParams) => void;
  autoGenerateParams?: AutoGenerateParams | null;
}

const STEP_LABELS: Record<WizardStep, string> = {
  user: "Select User",
  week: "Select Week",
  lens: `Select ${CONVERSATION_LENS}`,
  generate: "Generate Report"
};

const parseTimeRangeToWeekDates = (timeRange: string, timezone: string) => {
  const { startDate, endDate } = getTimeRangeFilter(timeRange, timezone);
  return { start: new Date(startDate), end: new Date(endDate) };
};

const WeeklyCoachingWizard = ({
  open,
  onClose,
  onReportRequested,
  autoGenerateParams
}: WeeklyCoachingWizardProps) => {
  const { tenantId, user } = useAuth();
  const { permittedEmails } = useSelectedGroups();
  const { account } = useAccount();
  const timezone = getUserTimezone();
  const queryClient = useQueryClient();

  const {
    selectedUser,
    selectedLens,
    selectedLensPath,
    weekStart,
    weekEnd,
    currentStep,
    isGenerating,
    isAutoGenerating,
    setSelectedUser,
    setSelectedLens,
    setWeekRange,
    setCurrentStep,
    setIsGenerating,
    setIsAutoGenerating,
    goToNextStep,
    goToPreviousStep,
    resetWizard,
    getWeekTimeRange,
    getStepIndex
  } = useWizardStore();

  const weekTimeRange = getWeekTimeRange();

  const { data: users = [], isLoading: isLoadingUsers } = useDistinctUsers({
    timeRange: "last30Days",
    permittedEmails
  });

  const { data: protocols = [], isLoading: isLoadingProtocols } = useLensProtocols({
    timeRange: weekTimeRange,
    permittedEmails
  });

  const {
    data: attributeExamples,
    isLoading: isLoadingExamples
  } = useAttributeExamples({
    timeRange: weekTimeRange,
    timezone,
    lensFilter: selectedLens || "all",
    userFilter: selectedUser || undefined,
    permittedEmails,
    tenantId,
    enabled: !!selectedLens && selectedLens !== "all" && !!selectedUser
  });

  const {
    fetchAllContext,
    getIsLoadingContext,
    getHasRequiredContext,
    resetCoachingContext,
    attributeStatsContent,
    teamBestPracticesContent
  } = useCoachingContextStore();

  const isLoadingContext = getIsLoadingContext();
  const hasRequiredContext = getHasRequiredContext();

  useEffect(() => {
    resetCoachingContext();
  }, [open, selectedUser, selectedLens, resetCoachingContext]);

  useEffect(() => {
    if (!selectedLens || selectedLens === "all" || !selectedUser) {
      return;
    }
    fetchAllContext({
      timeRange: weekTimeRange,
      protocol: selectedLens,
      protocolPath: selectedLensPath,
      user: selectedUser,
      permittedEmails,
      timezone,
      tenantId
    });
  }, [
    weekTimeRange,
    selectedLens,
    selectedLensPath,
    selectedUser,
    permittedEmails,
    timezone,
    tenantId,
    fetchAllContext
  ]);

  const {
    coachingLensTitle,
    coachingLensModel,
    coachingLensTemperature,
    coachingLensMaxTokens
  } = useCoachingHubPrompt();
  const { systemPrompt, customPrompt, setSystemPrompt } = useCoachingGeneratorStore();

  useEffect(() => {
    const prompt = hasCustomSystemPrompt()
      ? getSavedSystemPrompt()
      : generateSystemPrompt(account?.company_tone_and_language || undefined);
    setSystemPrompt(prompt);
  }, [account?.company_tone_and_language, setSystemPrompt]);

  const autoUser = autoGenerateParams?.user;
  const autoProtocol = autoGenerateParams?.protocol;
  const autoTimeRange = autoGenerateParams?.timeRange;

  useEffect(() => {
    if (!open) {
      return;
    }
    if (autoUser && autoTimeRange) {
      setSelectedUser(autoUser);
      const { start, end } = parseTimeRangeToWeekDates(autoTimeRange, timezone);
      setWeekRange(start, end);
      setCurrentStep(WIZARD_STEP_GENERATE);
      setIsAutoGenerating(true);
    } else {
      resetWizard();
    }
  }, [
    open,
    autoUser,
    autoProtocol,
    autoTimeRange,
    timezone,
    setSelectedUser,
    setCurrentStep,
    setIsAutoGenerating,
    setWeekRange,
    resetWizard
  ]);

  const autoProtocolName = autoGenerateParams?.protocol;
  useEffect(() => {
    if (!open || !autoProtocolName || selectedLens || protocols.length === 0) {
      return;
    }
    const matched = protocols.find((p) => p.name === autoProtocolName);
    if (matched) {
      setSelectedLens(matched.name, matched.fullPath);
    }
  }, [open, autoProtocolName, selectedLens, protocols, setSelectedLens]);

  const hasDataAvailable = useMemo(() => {
    if (!attributeExamples || attributeExamples.length === 0) {
      return false;
    }
    return attributeExamples.some((example) => example.count > 0);
  }, [attributeExamples]);

  const {
    data: existingArtifacts = [],
    isFetching: isCheckingExisting
  } = useExistingCoachingArtifacts({
    tenantId,
    agentEmail: selectedUser,
    lensName: selectedLens,
    weekStart,
    weekEnd,
    enabled: currentStep === WIZARD_STEP_GENERATE
  });

  const performSubmit = useCallback(async () => {
    if (!selectedLens || !tenantId || !user?.id) {
      return;
    }
    if (!attributeStatsContent || !attributeExamples || !teamBestPracticesContent) {
      return;
    }
    setIsGenerating(true);

    const analysisData = buildPerformanceAnalysisData({
      attributeStatsContent,
      attributeExamples,
      selectedUserEmail: selectedUser,
      lensName: selectedLens,
      timeRange: weekTimeRange,
      timezone
    });
    const documents: { name: string; text: string }[] = [
      {
        name: DOCUMENT_NAME_AGENT_SCORECARDS_SUMMARY,
        text: convertToAgentPerformanceAnalysis(analysisData)
      },
      {
        name: DOCUMENT_NAME_TEAM_BENCHMARKS,
        text: teamBestPracticesContent
      }
    ];

    const selectedAttributes = (attributeExamples ?? [])
      .filter((example) => example.count > 0)
      .map((example) => example.attribute);

    const baseParameters: WeeklyCoachingReportParameters = {
      tenant_id: tenantId,
      user_id: user.id,
      agent_email: selectedUser,
      lens_name: selectedLens,
      lens_path: selectedLensPath || undefined,
      time_range: weekTimeRange,
      timezone,
      started_timestamp: weekStart.getTime(),
      ended_timestamp: weekEnd.getTime(),
      documents,
      selected_attributes: selectedAttributes,
      custom_prompt: customPrompt || undefined,
      system_prompt: systemPrompt,
      coaching_hub_title: coachingLensTitle || undefined,
      model: coachingLensModel || PRODUCTION_MODEL.id,
      temperature: coachingLensTemperature ?? DEFAULT_TEMPERATURE,
      max_tokens: coachingLensMaxTokens || DEFAULT_MAX_TOKENS
    };

    const submission = await submitWeeklyCoachingReport({
      parameters: baseParameters
    }).catch((err) => {
      logError("Failed to submit coaching report job", err);
      return null;
    });

    if (!submission) {
      setIsGenerating(false);
      return;
    }

    queryClient.invalidateQueries({ queryKey: ["background-jobs"] });
    queryClient.invalidateQueries({
      predicate: (query) => {
        const root = query.queryKey[0];
        return typeof root === "string" && root.startsWith("coaching-artifacts");
      }
    });

    onReportRequested?.({
      jobId: submission.job_id,
      user: selectedUser,
      protocol: selectedLens,
      timeRange: weekTimeRange
    });
    onClose();
    setIsGenerating(false);
  }, [
    selectedLens,
    selectedLensPath,
    selectedUser,
    tenantId,
    user?.id,
    weekTimeRange,
    timezone,
    customPrompt,
    systemPrompt,
    coachingLensTitle,
    coachingLensModel,
    coachingLensTemperature,
    coachingLensMaxTokens,
    weekStart,
    weekEnd,
    attributeStatsContent,
    teamBestPracticesContent,
    attributeExamples,
    queryClient,
    onReportRequested,
    onClose,
    setIsGenerating
  ]);

  const handleSubmit = useCallback(async () => {
    if (!selectedLens || !tenantId || !user?.id) {
      return;
    }
    if (existingArtifacts.length > 0) {
      return;
    }
    await performSubmit();
  }, [
    selectedLens,
    tenantId,
    user?.id,
    existingArtifacts,
    performSubmit
  ]);

  useEffect(() => {
    const ready = isAutoGenerating
      && !isLoadingExamples
      && !isLoadingContext
      && !isGenerating
      && hasDataAvailable
      && hasRequiredContext;
    if (ready) {
      setIsAutoGenerating(false);
      handleSubmit();
    }
  }, [
    isAutoGenerating,
    isLoadingExamples,
    isLoadingContext,
    isGenerating,
    hasDataAvailable,
    hasRequiredContext,
    handleSubmit,
    setIsAutoGenerating
  ]);

  const hasLensesAvailable = !isLoadingProtocols && protocols.length > 0;
  const showNoLensesWarning = !isLoadingProtocols && protocols.length === 0;
  const isCheckingLensData = !!selectedLens && isLoadingExamples;
  const showNoDataWarning = !!selectedLens && !isLoadingExamples && !hasDataAvailable;

  const canProceedToNext = useMemo(() => {
    const stepValidation: Record<WizardStep, boolean> = {
      user: !!selectedUser,
      week: hasLensesAvailable,
      lens: !!selectedLens && hasDataAvailable,
      generate: false
    };
    return stepValidation[currentStep];
  }, [currentStep, selectedUser, selectedLens, hasDataAvailable, hasLensesAvailable]);

  const renderStepContent = () => {
    const stepContent: Record<WizardStep, React.ReactNode> = {
      user: <SelectUser users={users} isLoadingUsers={isLoadingUsers} />,
      week: <SelectWeek isCheckingData={isLoadingProtocols} showNoLensesWarning={showNoLensesWarning} />,
      lens: (
        <SelectLens
          protocols={protocols}
          isLoadingProtocols={isLoadingProtocols}
          isCheckingData={isCheckingLensData}
          showNoDataWarning={showNoDataWarning}
        />
      ),
      generate: (
        <GenerateReport
          isLoadingExamples={isLoadingExamples}
          hasDataAvailable={hasDataAvailable}
          onSubmit={handleSubmit}
          existingArtifacts={existingArtifacts}
          isCheckingExisting={isCheckingExisting}
        />
      )
    };
    return stepContent[currentStep];
  };

  return (
    <Dialog
      open={open}
      onClose={isGenerating ? undefined : onClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isGenerating}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6" component="span">Weekly Coaching Guide</Typography>
        {!isGenerating && (
          <Button onClick={onClose} sx={{ minWidth: "auto" }}>
            <Close />
          </Button>
        )}
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={getStepIndex()} alternativeLabel sx={{ mb: 4 }}>
          {WIZARD_STEPS.map((step) => (
            <Step key={step}>
              <StepLabel>{STEP_LABELS[step]}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={goToPreviousStep}
          disabled={currentStep === WIZARD_STEP_USER || isGenerating}
        >
          Back
        </Button>
        {currentStep !== WIZARD_STEP_GENERATE && (
          <Button
            variant="contained"
            onClick={goToNextStep}
            disabled={!canProceedToNext || isGenerating}
          >
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default WeeklyCoachingWizard;
